import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import LoadingState from '../../components/LoadingState';
import { ArrowLeft, Sun, AlertTriangle, Clock, ClipboardList, Eye, Camera, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SECTION_META = {
  OVERDUE: { label: 'Overdue', icon: AlertTriangle, tone: 'text-red-600' },
  TODAY: { label: 'Due Today', icon: Clock, tone: 'text-amber-600' },
  REQUIRES_MY_ACTION: { label: 'Requires Action', icon: ClipboardList, tone: 'text-blue-600' },
  REVIEWS: { label: 'Awaiting Review', icon: Eye, tone: 'text-indigo-600' },
  FIELD_CAPTURE: { label: 'Field Capture', icon: Camera, tone: 'text-teal-600' },
  UPCOMING: { label: 'Upcoming', icon: CalendarClock, tone: 'text-slate-500' },
  SYNC_DATA_ATTENTION: { label: 'Data / AI Review', icon: Eye, tone: 'text-violet-600' },
};

const PortfolioMyDay = () => {
  const { portfolioId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/portfolios/${portfolioId}/myday`)
      .then((r) => setData(r.data))
      .catch((e) => {
        toast.error(e.response?.status === 404 ? 'Portfolio not found or not authorized' : 'Failed to load');
        navigate('/admin/portfolios');
      })
      .finally(() => setLoading(false));
  }, [portfolioId, navigate]);

  if (loading) return <Layout><LoadingState message="Gathering actions across authorized projects..." /></Layout>;
  if (!data) return null;

  return (
    <Layout>
      <div className="space-y-6" data-testid="portfolio-myday-page">
        <div className="flex items-center justify-between">
          <div>
            <button onClick={() => navigate(`/app/portfolio/${portfolioId}/command`)} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-1" data-testid="back-to-command"><ArrowLeft className="w-4 h-4" />Command Center</button>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2"><Sun className="w-6 h-6 text-amber-500" />Portfolio My Day</h1>
            <p className="text-sm text-slate-500">{data.totalActionable} actionable item(s) across {data.authorizedProjectCount} authorized project(s)</p>
          </div>
        </div>

        {data.perProject.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="myday-per-project">
            {data.perProject.map((p) => (
              <button key={p.projectId} onClick={() => navigate(`/app/projects/${p.projectId}/workspace`)} className="text-xs px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200">
                {p.name}: <b>{p.actionable}</b>
              </button>
            ))}
          </div>
        )}

        {data.sections.length === 0 ? (
          <div className="text-center py-16 text-slate-500" data-testid="myday-empty">Nothing actionable for you across these projects right now.</div>
        ) : (
          <div className="space-y-6">
            {data.sections.map((sec) => {
              const meta = SECTION_META[sec.section] || { label: sec.section, icon: ClipboardList, tone: 'text-slate-500' };
              return (
                <div key={sec.section} data-testid={`myday-section-${sec.section}`}>
                  <h2 className={`text-sm font-semibold uppercase tracking-wide mb-2 flex items-center gap-2 ${meta.tone}`}><meta.icon className="w-4 h-4" />{meta.label} <span className="text-slate-400">({sec.count})</span></h2>
                  <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
                    {sec.items.map((item, i) => (
                      <button key={i} onClick={() => item.link && navigate(item.link)} className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 text-left" data-testid="myday-item">
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 dark:text-white truncate">{item.title}</p>
                          <p className="text-xs text-slate-500 truncate">{item.projectName} · {item.sourceLabel}{item.number ? ` · ${item.number}` : ''}</p>
                        </div>
                        <span className="text-xs text-slate-400 shrink-0 ml-3">{item.reason}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <p className="text-xs text-slate-400 border-t border-slate-100 dark:border-slate-800 pt-4">{data.governanceNote}</p>
      </div>
    </Layout>
  );
};

export default PortfolioMyDay;
